import React, { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { motion, useInView } from "framer-motion";
import {
  FaPlane,
  FaExchangeAlt,
  FaUniversity,
  FaSyncAlt,
  FaCreditCard,
} from "react-icons/fa";
import { springBouncy, EXPO_OUT, fadeInUp } from "../utils/animations";

const SERVICES = [
  { key: "traveler", Icon: FaPlane },
  { key: "exchange", Icon: FaExchangeAlt },
  { key: "bank", Icon: FaUniversity },
  { key: "internal", Icon: FaSyncAlt },
  { key: "cards", Icon: FaCreditCard },
];

/* 3D Tilt Card */
function TiltCard({ children, className, i }) {
  const [tilt, setTilt] = useState({ x: 0, y: 0 });
  const [hovering, setHovering] = useState(false);
  const ref = useRef(null);

  const onMove = (e) => {
    const el = ref.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width - 0.5) * 12;
    const y = ((e.clientY - rect.top) / rect.height - 0.5) * -12;
    setTilt({ x: y, y: x });
  };

  return (
    <motion.div
      ref={ref}
      variants={fadeInUp}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, margin: "-60px" }}
      custom={i}
      onMouseMove={onMove}
      onMouseEnter={() => setHovering(true)}
      onMouseLeave={() => { setHovering(false); setTilt({ x: 0, y: 0 }); }}
      animate={{
        rotateX: tilt.x,
        rotateY: tilt.y,
        scale: hovering ? 1.03 : 1,
      }}
      transition={{ type: "spring", stiffness: 300, damping: 20 }}
      style={{ transformStyle: "preserve-3d", perspective: 800 }}
      className={className}
    >
      {children}
      {/* Spotlight follow effect */}
      <motion.div
        className="absolute inset-0 rounded-2xl pointer-events-none"
        animate={{
          background: hovering
            ? `radial-gradient(circle at ${50 + tilt.y * 4}% ${50 - tilt.x * 4}%, rgba(201,162,39,0.08) 0%, transparent 60%)`
            : "radial-gradient(circle at 50% 50%, transparent 0%, transparent 60%)",
        }}
        transition={{ duration: 0.3 }}
      />
    </motion.div>
  );
}

export default function Services() {
  const { t } = useTranslation();
  const headingRef = useRef(null);
  const inView = useInView(headingRef, { once: true, margin: "-100px" });

  return (
    <section id="services" className="relative bg-gray-50 dark:bg-main-light py-20 lg:py-28 overflow-hidden">
      {/* Decorative corner glow */}
      <div className="absolute -top-20 start-0 w-96 h-96 bg-highlight/5 rounded-full blur-[80px] animate-float-orb" />
      <div className="absolute -bottom-20 end-0 w-96 h-96 bg-highlight/5 rounded-full blur-[80px] animate-float-orb-delayed" />

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section heading with clip reveal */}
        <div ref={headingRef} className="text-center mb-14">
          <motion.h2
            initial={{ opacity: 0, y: 30, filter: "blur(8px)" }}
            animate={inView ? { opacity: 1, y: 0, filter: "blur(0px)" } : {}}
            transition={{ duration: 0.7, ease: EXPO_OUT }}
            className="text-3xl md:text-4xl font-bold text-main dark:text-white font-syne mb-4"
          >
            {t("services.title")}
          </motion.h2>
          <motion.div
            initial={{ scaleX: 0, opacity: 0 }}
            animate={inView ? { scaleX: 1, opacity: 1 } : {}}
            transition={{ duration: 0.8, delay: 0.3, ease: EXPO_OUT }}
            className="w-24 h-1 bg-gradient-to-r from-transparent via-highlight to-transparent mx-auto rounded-full"
          />
        </div>

        {/* Service cards grid with 3D tilt */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {SERVICES.map(({ key, Icon }, i) => (
            <TiltCard
              key={key}
              i={i}
              className="group relative bg-white dark:bg-white/[0.04] border border-gray-200 dark:border-white/10 rounded-2xl p-7 shadow-card dark:shadow-card-dark hover:border-highlight/30 hover:shadow-card-hover dark:hover:shadow-card-dark-hover transition-colors duration-500 text-center overflow-hidden cursor-default gradient-border"
            >
              <div className="relative z-10" style={{ transform: "translateZ(40px)" }}>
                {/* Icon with bounce-in */}
                <motion.div
                  whileHover={{ scale: 1.15, rotate: [0, -8, 8, -4, 0] }}
                  transition={springBouncy}
                  className="w-16 h-16 bg-highlight/10 border border-highlight/20 rounded-2xl flex items-center justify-center mx-auto mb-5 group-hover:bg-highlight/15 group-hover:border-highlight/30 group-hover:shadow-[0_0_20px_rgba(201,162,39,0.15)] transition-all duration-300"
                >
                  <Icon className="text-highlight text-2xl" />
                </motion.div>
                <h3 className="text-xl font-bold text-main dark:text-white font-cairo mb-2 group-hover:text-highlight transition-colors duration-300">
                  {t(`services.${key}.name`)}
                </h3>
                <p className="text-gray-400 dark:text-white/40 font-cairo text-sm leading-relaxed group-hover:text-gray-500 dark:group-hover:text-white/60 transition-colors duration-300">
                  {t(`services.${key}.desc`)}
                </p>
              </div>
            </TiltCard>
          ))}
        </div>
      </div>
    </section>
  );
}
